import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import LanguageLink from "../routing/LanguageLink";


import Lottie from "lottie-react";
import { motion } from "framer-motion";
import ScrollAnimation from "../../hooks/ScrollAnimation";
import SEOHead from "../section/SeoHead";
import loginAnimation from "../../assets/animations/login-animation.json";
import HelpAccessingSection from "../section/HelpAccessingSection";
import LoginHeroSection from "../section/LoginHeroSection";
import { useAuth } from "../context/AuthContext";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next"; // Added

const Login = () => {
  const { t } = useTranslation(); // Added
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login, role } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let deviceId = localStorage.getItem("device_id");
    if (!deviceId) {
      deviceId = `guest_${Math.random().toString(36).substr(2, 12)}_${Date.now()}`;
      localStorage.setItem("device_id", deviceId);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const result = await login(formData.email, formData.password);

    setIsSubmitting(false);

    if (result.success) {
      const roleDisplay = role === "office_owner" ? "Office Owner" : role || "User";
      Swal.fire({
        icon: "success",
        title: "Welcome Back!", // Could accept translation? SweetAlerts usually stay in English or need complex handling. I'll leave as is for now or translate if easy. Let's keep it simple for alerts.
        text: `Logged in as ${roleDisplay}. Redirecting...`,
        showConfirmButton: true,
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        allowOutsideClick: false,
      }).then(() => {
        switch (role) {
          case "office_owner":
            navigate("/profile/office");
            break;
          case "admin":
            navigate("/profile");
            break;
          case "client":
            navigate("/profile/client");
            break;
          case "staff":
          case "paralegal":
            navigate("/profile/paralegal");
            break;
          default:
            navigate("/");
        }
      });
    } else {
      Swal.fire({
        icon: "error",
        title: "Login Failed",
        text: result.message || "Invalid credentials",
        showConfirmButton: true,
        confirmButtonText: "OK",
        confirmButtonColor: "#dc2626",
      });
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Login - Citizen Hub",
    description: "Login to your Citizen Hub account to access your immigration services and manage your account.",
    mainEntity: {
      "@type": "LocalBusiness",
      name: "Citizen Hub",
      telephone: "+1 3108802133",
      email: "info@citizenhub.com",
    },
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Login - Citizen Hub Account Access"
        description="Login to your Citizen Hub account to access your immigration services, view your account information, and manage your immigration cases."
        keywords="login citizen hub, immigration account login, citizen hub login, immigration services account access"
        canonicalUrl="https://citiznhub.com/login"
        structuredData={structuredData}
      />
      <LoginHeroSection />
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollAnimation direction="left" delay={0.1}>
              <motion.div
                className="bg-white rounded-xl shadow-lg p-8"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
              >
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {t("auth.login.title")}
                  </h2>
                  <p className="text-gray-600">
                    {t("auth.login.subtitle")}
                  </p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                  >
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      {t("auth.login.email")} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="your@email.com"
                    />
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                  >
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                      {t("auth.login.password")} *
                    </label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="Enter your password"
                    />
                  </motion.div>
                  <motion.div
                    className="flex items-center justify-between"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.6 }}
                  >
                    <div className="flex items-center">
                      <input
                        id="remember-me"
                        name="remember-me"
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                        {t("auth.login.remember_me")}
                      </label>
                    </div>
                    <div className="text-sm">
                      <LanguageLink to="/forgot-password" className="font-medium text-blue-600 hover:text-blue-500">
                        {t("auth.login.forgot_password")}
                      </LanguageLink>
                    </div>
                  </motion.div>
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-all"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {isSubmitting ? "Signing In..." : t("auth.login.sign_in_button")}
                  </motion.button>
                </form>
                <motion.div
                  className="mt-8 text-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7, duration: 0.6 }}
                >
                  <p className="text-gray-600">
                    {t("auth.login.no_account")}{" "}
                    <LanguageLink to="/register" className="font-medium text-blue-600 hover:text-blue-500 transition-colors">
                      {t("auth.login.create_one")}
                    </LanguageLink>
                  </p>
                </motion.div>
              </motion.div>
            </ScrollAnimation>

            <ScrollAnimation direction="right" delay={0.2}>
              <motion.div
                className="flex flex-col items-center justify-center"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
              >
                <div className="w-full max-w-md">
                  <Lottie
                    animationData={loginAnimation}
                    loop={true}
                    autoplay={true}
                    style={{ width: "100%", height: "auto" }}
                  />
                </div>
                <motion.div
                  className="text-center mt-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.6 }}
                >
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    {t("auth.login.welcome_back")}
                  </h3>
                  <p className="text-gray-600">
                    {t("auth.login.welcome_text")}
                  </p>
                </motion.div>
              </motion.div>
            </ScrollAnimation>
          </div>
        </div>
      </section>
      <HelpAccessingSection />
    </div>
  );
};

export default Login;